import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/life-insurance-for-veterans-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

type VetAge = '25' | '30' | '35' | '40' | '45' | '50' | '55' | '60';
type VetCoverage = '100k' | '250k' | '500k' | '1m';
type VetHealth = 'excellent' | 'good' | 'fair';

interface VetResult {
  carrier: string;
  amBest: string;
  type: string;
  monthlyRate: number;
  rateClass: string;
  approvalTime: string;
  veteranNote: string;
  badge?: string;
}

const VET_BASE: Record<VetAge, Record<VetCoverage, number>> = {
  '25': { '100k': 10, '250k': 16, '500k': 23, '1m': 39 },
  '30': { '100k': 12, '250k': 19, '500k': 28, '1m': 48 },
  '35': { '100k': 15, '250k': 24, '500k': 36, '1m': 64 },
  '40': { '100k': 22, '250k': 36, '500k': 55, '1m': 97 },
  '45': { '100k': 34, '250k': 57, '500k': 88, '1m': 153 },
  '50': { '100k': 54, '250k': 90, '500k': 139, '1m': 245 },
  '55': { '100k': 88, '250k': 147, '500k': 227, '1m': 397 },
  '60': { '100k': 130, '250k': 218, '500k': 337, '1m': 590 },
};

const HEALTH_MULT: Record<VetHealth, number> = {
  excellent: 0.88,
  good: 1.0,
  fair: 1.35,
};

function getVetResults(age: VetAge, coverage: VetCoverage, health: VetHealth): VetResult[] {
  const base = VET_BASE[age][coverage];
  const hm = HEALTH_MULT[health];
  const m = Math.round(base * hm);
  return [
    {
      carrier: 'Banner Life',
      amBest: 'A+',
      type: '20-Year Term',
      monthlyRate: m,
      rateClass: health === 'excellent' ? 'Preferred Plus' : health === 'good' ? 'Preferred' : 'Standard Plus',
      approvalTime: '24–48 hrs',
      veteranNote: 'Best rates for veterans with no service-related conditions. Accelerated underwriting — no exam up to $1M.',
      badge: 'Best for Veterans',
    },
    {
      carrier: 'Protective Life',
      amBest: 'A+',
      type: '20-Year Term',
      monthlyRate: Math.round(m * 1.04),
      rateClass: health === 'excellent' ? 'Preferred Plus' : health === 'good' ? 'Preferred' : 'Standard Plus',
      approvalTime: '24–72 hrs',
      veteranNote: 'Excellent for veterans with controlled PTSD or mild TBI. Lenient underwriting for mental health history.',
    },
    {
      carrier: 'Pacific Life',
      amBest: 'A+',
      type: '20-Year Term',
      monthlyRate: Math.round(m * 1.07),
      rateClass: health === 'excellent' ? 'Preferred Plus' : health === 'good' ? 'Preferred' : 'Standard',
      approvalTime: '1–2 weeks',
      veteranNote: 'Strong for veterans with Agent Orange exposure history. Good underwriting for Vietnam-era veterans.',
    },
    {
      carrier: 'Prudential',
      amBest: 'A+',
      type: '20-Year Term',
      monthlyRate: Math.round(m * 1.10),
      rateClass: health === 'excellent' ? 'Preferred' : health === 'good' ? 'Standard Plus' : 'Standard',
      approvalTime: '1–3 weeks',
      veteranNote: 'Best for veterans with complex health histories including PTSD, TBI, or multiple service-related conditions.',
    },
    {
      carrier: 'Mutual of Omaha',
      amBest: 'A+',
      type: '20-Year Term',
      monthlyRate: Math.round(m * 1.12),
      rateClass: health === 'excellent' ? 'Preferred' : health === 'good' ? 'Standard Plus' : 'Standard',
      approvalTime: '1–2 weeks',
      veteranNote: 'Veteran-friendly underwriting. Good for Indianapolis veterans with controlled conditions from service.',
    },
  ];
}

const VGLI_COMPARISON = [
  { factor: 'Monthly Cost ($400K, age 40)', vgli: '$308/mo', private: '$55/mo', winner: 'private' },
  { factor: 'Monthly Cost ($400K, age 50)', vgli: '$600/mo', private: '$139/mo', winner: 'private' },
  { factor: 'Monthly Cost ($400K, age 60)', vgli: '$1,200/mo', private: '$337/mo', winner: 'private' },
  { factor: 'Coverage Maximum', vgli: '$500,000', private: 'Unlimited', winner: 'private' },
  { factor: 'Portability', vgli: 'Yes — continues after service', private: 'Yes — independent of employer', winner: 'tie' },
  { factor: 'Medical Exam Required', vgli: 'No (if converted from SGLI)', private: 'Often no (accelerated UW)', winner: 'tie' },
  { factor: 'Rate Increases with Age', vgli: 'Yes — significant increases', private: 'No — locked in at issue', winner: 'private' },
  { factor: 'Cash Value / Investment', vgli: 'No', private: 'Available (whole life/IUL)', winner: 'private' },
];

const CLUSTER_LINKS = [
  { to: '/term-life-insurance-quotes-indianapolis', label: 'Term Life Insurance Quotes Indianapolis', icon: 'ri-calculator-line' },
  { to: '/life-insurance-rates-indianapolis', label: 'Life Insurance Rates Indianapolis', icon: 'ri-line-chart-line' },
  { to: '/no-exam-life-insurance-indianapolis', label: 'No Exam Life Insurance Indianapolis', icon: 'ri-stethoscope-line' },
  { to: '/best-life-insurance-indianapolis', label: 'Best Life Insurance Indianapolis', icon: 'ri-award-line' },
  { to: '/affordable-life-insurance-indianapolis', label: 'Affordable Life Insurance Indianapolis', icon: 'ri-money-dollar-circle-line' },
  { to: '/life-insurance-agent-indianapolis', label: 'Life Insurance Agent Indianapolis', icon: 'ri-user-star-line' },
  { to: '/life-insurance-for-diabetics-indianapolis', label: 'Life Insurance for Diabetics Indianapolis', icon: 'ri-heart-pulse-line' },
  { to: '/life-insurance-smokers-indianapolis', label: 'Life Insurance for Smokers Indianapolis', icon: 'ri-forbid-line' },
  { to: '/cheap-life-insurance-indianapolis', label: 'Cheap Life Insurance Indianapolis', icon: 'ri-price-tag-3-line' },
  { to: '/life-insurance-quotes-indianapolis', label: 'Life Insurance Quotes Indianapolis', icon: 'ri-search-line' },
  { to: '/term-life-insurance-indianapolis', label: 'Term Life Insurance Indianapolis', icon: 'ri-calendar-check-line' },
  { to: '/life-insurance-for-families-indianapolis', label: 'Life Insurance for Families Indianapolis', icon: 'ri-home-heart-line' },
];

const FAQS = [
  {
    q: 'Can Indianapolis veterans get private life insurance?',
    a: 'Yes — Indianapolis veterans can absolutely get private life insurance, and in most cases, private life insurance is significantly cheaper than VGLI (Veterans Group Life Insurance). For example, a 40-year-old veteran pays $308/month for $400K VGLI coverage. The same $400K 20-year term policy from Banner Life costs about $55/month — an $253/month savings. The key: private life insurance rates are based on your current health, not your veteran status. If you are in good health, you can get excellent rates from private carriers. If you have service-related health conditions (PTSD, TBI, Agent Orange exposure), an independent agent can find the most lenient carrier for your specific situation.',
  },
  {
    q: 'Does PTSD affect life insurance rates for Indianapolis veterans?',
    a: 'PTSD can affect life insurance rates for Indianapolis veterans, but it does not automatically disqualify you. Here\'s how carriers typically handle PTSD: Mild PTSD (well-controlled, no hospitalizations, stable medication): Standard Plus to Preferred rate class — minimal impact on rates. Moderate PTSD (some hospitalizations, stable for 2+ years): Standard rate class — 25–50% above Preferred rates. Severe PTSD (recent hospitalizations, multiple medications, disability rating): Table 2–4 rate class — 50–100% above Preferred rates. The best carriers for Indianapolis veterans with PTSD: Protective Life and Prudential have the most lenient underwriting for mental health conditions. An independent agent can shop your case to find the best rate.',
  },
  {
    q: 'Is VGLI worth it for Indianapolis veterans?',
    a: 'VGLI (Veterans Group Life Insurance) is generally NOT worth it for most Indianapolis veterans — especially those under age 60. Here\'s why: VGLI rates increase every 5 years and become extremely expensive as you age. A 60-year-old veteran pays $1,200/month for $400K VGLI coverage. The same $400K 20-year term policy from a private carrier costs about $337/month — a $863/month savings. VGLI makes sense only if: 1. You have serious health conditions that prevent you from qualifying for private life insurance. 2. You are over age 75 and private coverage is unavailable. For most Indianapolis veterans in good to fair health, replacing VGLI with a private term life policy saves thousands of dollars per year.',
  },
  {
    q: 'Does Agent Orange exposure affect life insurance rates?',
    a: 'Agent Orange exposure can affect life insurance rates for Indianapolis veterans, depending on whether it has caused any health conditions. If you have Agent Orange exposure but no resulting health conditions: Most carriers will approve you at standard rates with no surcharge. If you have Agent Orange-related conditions (diabetes, heart disease, certain cancers): Rates depend on the specific condition and how well it is controlled. Pacific Life and Prudential have the most experience underwriting Agent Orange-related conditions. If you have multiple Agent Orange-related conditions: Simplified issue or guaranteed issue life insurance may be the best option. An independent agent can review your specific situation and find the best carrier for your Agent Orange history.',
  },
  {
    q: 'What life insurance options are available for disabled Indianapolis veterans?',
    a: 'Indianapolis veterans with VA disability ratings have several life insurance options: 1. Service-Disabled Veterans Life Insurance (S-DVI): VA-administered, up to $10,000 coverage, very low rates. Available to veterans with service-connected disabilities. 2. Veterans Affairs Life Insurance (VALife): New VA program for veterans with any disability rating. Up to $40,000 whole life coverage. 3. Private life insurance: Many carriers will approve disabled veterans at standard or near-standard rates, depending on the specific disability. A 30% disability rating for a knee injury, for example, has minimal impact on private life insurance rates. 4. Simplified issue or guaranteed issue: For veterans with serious service-related conditions who cannot qualify for traditional private life insurance. Call (317) 648-2983 to discuss your specific disability rating and find the best coverage option.',
  },
  {
    q: 'How much life insurance do Indianapolis veterans need?',
    a: 'The right amount of life insurance for Indianapolis veterans depends on your financial obligations. The DIME method: Debt (all debts including mortgage, car, credit cards) + Income (annual income × 10–12 years) + Mortgage (remaining balance) + Education (college costs for each child). For a typical Indianapolis veteran family: $75,000 income × 10 = $750,000. Plus $250,000 mortgage = $1,000,000 total. A $1M 20-year term policy for a healthy 35-year-old veteran costs about $75/month — far less than VGLI for the same coverage. Important: Don\'t rely solely on VGLI or employer group life insurance. These policies end when you leave service or change jobs. Private life insurance stays with you regardless of employment status.',
  },
];

const TESTIMONIALS = [
  {
    name: 'Marcus W.',
    location: 'Indianapolis, IN (Army Veteran)',
    quote: 'I was paying $308/month for VGLI. Benjamin found me $500K private term for $55/month. I\'m saving $253/month and have more coverage. Wish I\'d done this years ago.',
    stars: 5,
    coverage: '$500K 20-year term',
    savings: 'Saves $253/month vs. VGLI',
  },
  {
    name: 'Jennifer R.',
    location: 'Carmel, Indianapolis (Navy Veteran)',
    quote: 'I have PTSD and was worried no one would insure me. Benjamin found me Protective Life at standard rates. The process was respectful and straightforward. Highly recommend.',
    stars: 5,
    coverage: '$500K 20-year term',
    savings: 'Approved with PTSD history',
  },
  {
    name: 'David K.',
    location: 'Fishers, Indianapolis (Marine Veteran)',
    quote: 'Vietnam vet with Agent Orange exposure. Benjamin found me Pacific Life at standard rates. My family is protected and I\'m paying a fraction of what VGLI would cost at my age.',
    stars: 5,
    coverage: '$250K 20-year term',
    savings: 'Agent Orange — standard rates',
  },
  {
    name: 'Sarah & Tom B.',
    location: 'Noblesville, Indianapolis (Air Force Veterans)',
    quote: 'Both veterans, both got approved for $500K each at Preferred rates. Benjamin compared 8 carriers and found us the best deal. The whole process took 30 minutes.',
    stars: 5,
    coverage: '$500K each, 20-year term',
    savings: '$43/month each at age 32',
  },
];

export default function LifeInsuranceForVeteransIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [vetAge, setVetAge] = useState<VetAge>('35');
  const [vetCoverage, setVetCoverage] = useState<VetCoverage>('500k');
  const [vetHealth, setVetHealth] = useState<VetHealth>('good');
  const [results, setResults] = useState<VetResult[] | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const title = 'Life Insurance for Veterans Indianapolis IN | VGLI vs. Private Rates | Hoosier Life Insurance';
    const description = 'Life insurance for veterans in Indianapolis, IN. Compare VGLI vs. private rates. PTSD, TBI, Agent Orange — all considered. Save up to $253/month vs. VGLI. Call (317) 648-2983.';
    const keywords = 'life insurance for veterans Indianapolis, veteran life insurance Indianapolis, VGLI alternative Indianapolis, life insurance PTSD Indianapolis';
    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/779d1d2415305e5405d8250cd6356729.jpg' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: '2026-04-12T00:00:00Z',
        isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Life Insurance Indianapolis', item: `${SITE_URL}/life-insurance-indianapolis` },
          { '@type': 'ListItem', position: 3, name: 'Life Insurance for Veterans Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SpeakableSpecification',
        cssSelector: ['#veterans-stats-bar', '#veterans-vgli-comparison'],
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  const ageOptions: VetAge[] = ['25', '30', '35', '40', '45', '50', '55', '60'];
  const coverageOptions: { value: VetCoverage; label: string }[] = [
    { value: '100k', label: '$100K' }, { value: '250k', label: '$250K' },
    { value: '500k', label: '$500K' }, { value: '1m', label: '$1M' },
  ];
  const healthOptions: { value: VetHealth; label: string; desc: string }[] = [
    { value: 'excellent', label: 'Excellent', desc: 'No service-related conditions' },
    { value: 'good', label: 'Good', desc: 'Controlled conditions (PTSD, etc.)' },
    { value: 'fair', label: 'Fair', desc: 'Multiple service-related conditions' },
  ];

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/life-insurance-indianapolis" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">Life Insurance Indianapolis</Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Life Insurance for Veterans</span>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden min-h-[560px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Indianapolis%20Indiana%20military%20veteran%20man%20in%20casual%20clothes%20smiling%20at%20home%20with%20family%20warm%20living%20room%20natural%20light%20American%20flag%20subtle%20background%20patriotic%20proud%20financial%20security%20life%20insurance%20photorealistic%20warm%20neutral%20tones%20wide%20angle&width=1440&height=700&seq=vet-li-indy-hero-2025a&orientation=landscape"
            alt="Life Insurance for Veterans Indianapolis Indiana — VGLI vs. Private Rates"
            title="Life Insurance for Veterans in Indianapolis, Indiana"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/96 via-[#1A3A52]/85 to-[#1A3A52]/30"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[720px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-shield-star-line text-sm"></i>
              Indianapolis Veterans · VGLI vs. Private · PTSD &amp; TBI Friendly · Agent Orange Considered
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Life Insurance for Indiana Veterans —{' '}
              <span className="text-[#D97642]">Beyond VA Coverage</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 max-w-[640px]">
              Most Indianapolis veterans overpay for VGLI. Private term life insurance is <strong className="text-white">up to 80% cheaper</strong> — with better coverage. PTSD, TBI, Agent Orange, and service-related conditions all considered.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { value: '80%', label: 'Potential savings vs. VGLI for Indianapolis veterans' },
                { value: '$253/mo', label: 'Average savings when switching from VGLI at age 40' },
                { value: '5', label: 'Veteran-friendly carriers compared for Indianapolis' },
                { value: 'PTSD OK', label: 'PTSD, TBI, and service conditions all considered' },
              ].map(item => (
                <div key={item.label} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-4 py-3 text-center">
                  <div className="text-xl font-extrabold text-[#D97642]">{item.value}</div>
                  <div className="text-xs text-white/80 mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="#vet-finder" className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-calculator-line text-lg"></i>
                Compare My Veteran Rates
              </a>
              <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
              <button
                onClick={() => {
                  const widget = document.querySelector('readdy-agent') as HTMLElement & { open?: () => void };
                  if (widget && typeof widget.open === 'function') {
                    widget.open();
                  } else {
                    const btn = document.querySelector('.readdy-agent-button') as HTMLElement;
                    if (btn) btn.click();
                  }
                }}
                className="inline-flex items-center gap-2 px-6 py-4 bg-white/15 hover:bg-white/25 border border-white/30 hover:border-white/50 text-white text-base font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm"
              >
                <i className="ri-chat-3-line text-lg"></i>
                Chat with us
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section id="veterans-stats-bar" className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '80%', label: 'Potential savings vs. VGLI — private term life is dramatically cheaper' },
              { value: '$253/mo', label: 'Average savings for a 40-year-old veteran switching from VGLI' },
              { value: 'PTSD OK', label: 'PTSD, TBI, Agent Orange, and service conditions all considered' },
              { value: '5', label: 'Veteran-friendly carriers compared for Indianapolis applicants' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MAIN CONTENT + SIDEBAR */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">
          <div>

            {/* VGLI vs. Private Comparison */}
            <div id="veterans-vgli-comparison" className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">VGLI vs. Private Life Insurance</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                VGLI vs. Private Life Insurance — Indianapolis Veterans
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Most Indianapolis veterans are dramatically overpaying for VGLI. Here's how VGLI compares to private term life insurance:
              </p>
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="grid grid-cols-4 bg-[#1A3A52] px-6 py-3 text-xs font-bold text-white/70 uppercase tracking-wide">
                  <span className="col-span-2">Factor</span>
                  <span className="text-center">VGLI</span>
                  <span className="text-center">Private Term</span>
                </div>
                {VGLI_COMPARISON.map((row, i) => (
                  <div key={i} className={`grid grid-cols-4 px-6 py-4 border-b border-gray-100 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <span className="col-span-2 text-sm font-semibold text-[#1A3A52]">{row.factor}</span>
                    <span className={`text-center text-sm ${row.winner === 'private' ? 'text-red-500' : 'text-gray-600'}`}>{row.vgli}</span>
                    <span className={`text-center text-sm font-bold ${row.winner === 'private' ? 'text-[#4A7C59]' : 'text-gray-600'}`}>{row.private}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* VETERAN RATE FINDER */}
            <div id="vet-finder" className="mb-16 scroll-mt-24">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Veteran Rate Finder</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                Compare Private Life Insurance Rates — Indianapolis Veterans
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Select your age, coverage, and health to see private life insurance rates from the top 5 veteran-friendly carriers.
              </p>

              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-[#1A3A52] px-7 py-7">
                  <h3 className="text-base font-bold text-white mb-6 flex items-center gap-2">
                    <i className="ri-shield-star-line text-[#D97642]"></i>
                    Your Veteran Profile
                  </h3>

                  <div className="mb-5">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Your Age</label>
                    <div className="flex flex-wrap gap-2">
                      {ageOptions.map(a => (
                        <button key={a} onClick={() => { setVetAge(a); setResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${vetAge === a ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Coverage Amount</label>
                    <div className="flex flex-wrap gap-2">
                      {coverageOptions.map(opt => (
                        <button key={opt.value} onClick={() => { setVetCoverage(opt.value); setResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${vetCoverage === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-7">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Overall Health</label>
                    <div className="grid grid-cols-3 gap-3">
                      {healthOptions.map(opt => (
                        <button key={opt.value} onClick={() => { setVetHealth(opt.value); setResults(null); }}
                          className={`px-4 py-3 rounded-xl text-left transition-all cursor-pointer ${vetHealth === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          <div className="text-sm font-bold">{opt.label}</div>
                          <div className={`text-xs mt-0.5 ${vetHealth === opt.value ? 'text-white/80' : 'text-white/50'}`}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button onClick={() => setResults(getVetResults(vetAge, vetCoverage, vetHealth))}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold text-base">
                    <i className="ri-calculator-line text-lg"></i>
                    Compare My Veteran Rates
                  </button>
                </div>

                {results !== null && (
                  <div className="px-7 py-7">
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                      <h3 className="text-base font-bold text-[#1A3A52]">
                        Veteran Rates — Age {vetAge} · {vetHealth.charAt(0).toUpperCase() + vetHealth.slice(1)} Health
                      </h3>
                      <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#4A7C59]/10 text-[#4A7C59] whitespace-nowrap">
                        From ${Math.min(...results.map(r => r.monthlyRate))}/mo
                      </span>
                    </div>
                    <div className="flex flex-col gap-4 mb-6">
                      {results.map((r, i) => (
                        <div key={r.carrier} className={`rounded-xl border p-5 ${i === 0 ? 'border-[#4A7C59]/30 bg-[#F0FFF4]' : 'border-gray-100 bg-gray-50'}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 flex items-center justify-center rounded-full text-white text-sm font-bold flex-shrink-0 ${i === 0 ? 'bg-[#4A7C59]' : 'bg-[#1A3A52]/30'}`}>{i + 1}</div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-bold text-[#1A3A52]">{r.carrier}</span>
                                  {r.badge && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#4A7C59] text-white whitespace-nowrap">{r.badge}</span>}
                                  <span className="text-xs bg-[#1A3A52]/10 text-[#1A3A52] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">AM Best {r.amBest}</span>
                                </div>
                                <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{r.veteranNote}</p>
                                <div className="flex items-center gap-3 mt-1 flex-wrap">
                                  <span className="text-xs text-gray-400">Rate class: {r.rateClass}</span>
                                  <span className="text-xs text-gray-400">Approval: {r.approvalTime}</span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className={`text-xl font-extrabold ${i === 0 ? 'text-[#4A7C59]' : 'text-[#1A3A52]'}`}>${r.monthlyRate}<span className="text-sm font-normal">/mo</span></div>
                              <div className="text-xs text-gray-400">${(r.monthlyRate * 12).toLocaleString()}/yr</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => setShowBookingModal(true)}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                      <i className="ri-calendar-check-line text-lg"></i>
                      Lock In My Veteran Rate — Free 15-Min Call
                    </button>
                  </div>
                )}

                {results === null && (
                  <div className="px-7 py-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D97642]/10 mx-auto mb-4">
                      <i className="ri-shield-star-line text-3xl text-[#D97642]"></i>
                    </div>
                    <p className="text-sm text-gray-500">Select your age, coverage, and health above, then click <strong className="text-[#1A3A52]">Compare My Veteran Rates</strong>.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Stories</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Indianapolis Veterans Who Saved by Switching from VGLI
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span className="bg-[#D97642]/10 text-[#D97642] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.coverage}</span>
                      <span className="bg-[#4A7C59]/10 text-[#4A7C59] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.savings}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>)}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Internal Linking Cluster */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Indianapolis Life Insurance Guide</span>
              <h2 className="text-2xl font-bold text-[#1A3A52] mb-4 leading-tight">Explore All Indianapolis Life Insurance Topics</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {CLUSTER_LINKS.map(link => (
                  <Link key={link.to} to={link.to} className="flex items-center gap-3 px-4 py-3.5 bg-white rounded-xl border border-gray-100 hover:border-[#D97642]/30 hover:bg-[#FFF8F4] transition-all group cursor-pointer">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-[#1A3A52]">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Life Insurance for Veterans Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors">
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug"><a id={`faq-vet-indy-${i}`}>{faq.q}</a></h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-shield-star-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Veteran Rate Comparison</h3>
                    <p className="text-xs text-white/70">PTSD &amp; TBI friendly · 20+ carriers</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Veteran Consultation
                  </button>
                  <a href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold">
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                </div>
              </div>
            </div>

            {/* VGLI vs. Private Quick Comparison */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                <i className="ri-scales-3-line text-[#D97642]"></i>
                VGLI vs. Private — $400K Coverage
              </h3>
              <div className="flex flex-col gap-2">
                {[
                  { age: 'Age 35', vgli: '$156/mo', private: '$36/mo', savings: '$120/mo' },
                  { age: 'Age 40', vgli: '$308/mo', private: '$55/mo', savings: '$253/mo' },
                  { age: 'Age 45', vgli: '$480/mo', private: '$88/mo', savings: '$392/mo' },
                  { age: 'Age 50', vgli: '$600/mo', private: '$139/mo', savings: '$461/mo' },
                  { age: 'Age 55', vgli: '$900/mo', private: '$227/mo', savings: '$673/mo' },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <div className="text-xs font-semibold text-[#1A3A52]">{row.age}</div>
                      <div className="text-xs text-gray-400">VGLI: {row.vgli} · Private: {row.private}</div>
                    </div>
                    <span className="text-sm font-bold text-[#4A7C59]">Save {row.savings}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">Private rates: Banner Life, Preferred Plus, male non-smoker. VGLI rates: official VA schedule.</p>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free Veteran Rate Call</h3>
                    <p className="text-xs text-white/70">PTSD &amp; TBI friendly · No pressure</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Free Veteran Rate Call',
                    step0Title: 'Pick a Date', step1Title: 'Choose a Time', step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date', selectTime: 'See Available Times →', reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev', nextMonth: 'Next', noSlotsAvailable: 'No slots available',
                    selectedDateTime: 'Your Appointment', modifyTime: 'Change',
                    customerName: 'Full Name', customerPhone: 'Phone Number',
                    notes: 'Branch of service or coverage questions? (Optional)',
                    submitButton: 'Confirm My Free Veteran Rate Call', submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.', backButton: '← Back to Calendar', required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Indianapolis veterans deserve better than VGLI rates
          </h2>
          <p className="text-base text-white/75 mb-7">
            Private term life insurance is up to 80% cheaper than VGLI — with better coverage. PTSD, TBI, and Agent Orange all considered. Free comparison, no obligation.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#vet-finder" className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-calculator-line text-lg"></i>
              Compare My Veteran Rates
            </a>
            <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={128} productName="Life Insurance for Veterans Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="Veteran Life Insurance" quoteHref="/instant-quote" />

      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Life Insurance for Veterans — Indianapolis</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance" style={{ width: '100%', height: '600px', border: 'none' }} title="Book Appointment" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
